import styled from 'styled-components'
import { orange, blue, gold, red, danger, textMuted, white } from '@/theme/colors'
import { shadows, radii, media } from '@/theme/tokens'
import { fontSizes, text, leading } from '@/theme/typography'

/** Only rendered when answers in range belong to nobody — see the page docblock. */
export const WarningStrip = styled.div`
	display: flex;
	gap: 10px;
	align-items: flex-start;
	background-color: ${gold}1f;
	border: 1px solid ${gold}66;
	border-radius: ${radii.inner};
	padding: 14px 16px;
	margin-bottom: 24px;
	color: ${blue};
	font-size: ${fontSizes.small};
	line-height: ${leading.body};
`

export const CohortNote = styled.p`
	margin: 0 0 20px;
	color: ${textMuted};
	font-size: ${fontSizes.base};
`

export const CoverageGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: 16px;
	margin-bottom: 32px;

	${media.tabletWide} {
		grid-template-columns: repeat(2, 1fr);
	}
`

export const Tile = styled.div`
	background-color: ${white};
	border-radius: ${radii.inner};
	box-shadow: ${shadows.inner};
	padding: 20px 24px;
	display: flex;
	flex-direction: column;
	gap: 6px;
	min-width: 0;
`

export const TileLabel = styled.span`
	font-size: ${fontSizes.small};
	font-weight: 500;
	color: ${textMuted};
`

export const TileCount = styled.span`
	font-size: ${fontSizes.display};
	font-weight: 700;
	color: ${blue};
	line-height: ${leading.none};
`

export const TileShare = styled.span`
	font-size: ${fontSizes.base};
	font-weight: 700;
	color: ${orange};
`

export const Section = styled.section`
	background-color: ${white};
	border-radius: ${radii.inner};
	box-shadow: ${shadows.inner};
	padding: 24px;
	margin-bottom: 24px;
	min-width: 0;
`

export const SectionTitle = styled.h2`
	margin: 0;
	font-size: ${fontSizes.body};
	font-weight: 700;
	color: ${blue};
`

export const SectionSubtitle = styled.p`
	margin: 4px 0 20px;
	font-size: ${fontSizes.small};
	color: ${textMuted};
`

/** The two distributions sit side by side on wide screens, stacked below. */
export const DistributionGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 24px;

	${media.tabletWide} {
		grid-template-columns: 1fr;
	}
`

export const BarRow = styled.div`
	display: grid;
	grid-template-columns: minmax(90px, 26%) 1fr auto;
	align-items: center;
	gap: 12px;
	padding: 4px 0;
`

export const BarLabel = styled.span<{ $unknown?: boolean }>`
	font-size: ${fontSizes.small};
	color: ${({ $unknown }) => ($unknown ? danger : blue)};
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`

export const BarTrack = styled.div`
	position: relative;
	height: 10px;
	border-radius: ${radii.round};
	background-color: ${textMuted}14;
	overflow: hidden;
`

export const BarFill = styled.div<{ $width: number; $unknown?: boolean }>`
	position: absolute;
	inset: 0 auto 0 0;
	width: ${({ $width }) => `${$width}%`};
	border-radius: ${radii.round};
	background-color: ${({ $unknown }) => ($unknown ? red : orange)};
`

export const BarValue = styled.span`
	font-size: ${fontSizes.small};
	font-variant-numeric: tabular-nums;
	color: ${textMuted};
	white-space: nowrap;
`

export const UnknownTag = styled.span`
	margin-left: 6px;
	${text.eyebrow}
	color: ${danger};
`


